import { Trans, useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { ArrowRightIcon } from "lucide-react";
import { useId } from "react";

import { cn } from "@/shared/lib";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/shared/ui/sidebar";
import { Skeleton } from "@/shared/ui/skeleton";

import { useHeroLookup } from "../../api/useHeroLookup";
import { useMatches } from "../../api/useMatches";
import { HeroAvatar } from "../HeroAvatar";
import { MatchScoreBadge } from "../MatchScoreBadge";
import { FreshMatchesStatus } from "./FreshMatchesStatus";

const LIMIT = 4;

// «Последние матчи» в боковом меню по макету design/prototype/Sidebar.dc.html: переход
// к разбору любого из последних матчей с любой страницы. Тот же кэш, что у сводки главной.
// Под матчами — как они приходят (`FreshMatchesStatus`, макет «15г»).
// Пока список грузится — заглушки, матчей нет — только как приходят матчи. В свёрнутом меню остаётся иконка
// героя с точкой исхода и подсказка.
export const RecentMatchesNav = () => {
  const { i18n, t } = useLingui();
  const labelId = useId();
  const lookup = useHeroLookup();
  const { query, items } = useMatches();

  if (query.isPending) {
    return (
      <div
        aria-hidden="true"
        className="flex w-full flex-col gap-2 px-2.5 group-data-[state=collapsed]/sidebar:items-center group-data-[state=collapsed]/sidebar:px-0"
      >
        {Array.from({ length: LIMIT }, (_, index) => (
          <Skeleton
            key={index}
            className="h-10 w-full group-data-[state=collapsed]/sidebar:size-9"
          />
        ))}
      </div>
    );
  }
  if (items.length === 0) {
    // Матчей ещё нет — как раз когда задержка в 2–3 дня важнее всего: ссылка остаётся.
    return (
      <SidebarGroup aria-label={t`Matches`}>
        <FreshMatchesStatus />
      </SidebarGroup>
    );
  }

  const formatDay = new Intl.DateTimeFormat(i18n.locale, { day: "numeric", month: "short" });
  return (
    <SidebarGroup aria-labelledby={labelId}>
      <SidebarGroupLabel id={labelId}>
        <Trans>Recent matches</Trans>
      </SidebarGroupLabel>
      <SidebarMenu>
        {items.slice(0, LIMIT).map((match) => {
          const hero = lookup(match.hero_id);
          const heroName = hero.name;
          const result = match.won ? t`victory` : t`defeat`;
          const day = formatDay.format(new Date(match.started_at));
          const score = match.score;
          const tooltip =
            score === null
              ? t`${heroName}, ${result}, ${day}`
              : t`${heroName}, ${result}, ${day}, score ${score} of 100`;
          return (
            <SidebarMenuItem key={match.match_id}>
              <SidebarMenuButton asChild size="lg" tooltip={tooltip}>
                <Link
                  to="/matches/$matchId"
                  params={{ matchId: match.match_id }}
                  data-match-id={match.match_id}
                >
                  <span className="relative shrink-0">
                    <HeroAvatar name={heroName} iconUrl={hero.iconUrl} size={36} />
                    <span
                      aria-hidden="true"
                      className={cn(
                        "absolute -right-0.5 -bottom-0.5 hidden size-3 rounded-full border-2 border-sidebar group-data-[state=collapsed]/sidebar:block",
                        match.won ? "bg-good" : "bg-bad",
                      )}
                    />
                  </span>
                  <span className="flex min-w-0 grow flex-col leading-tight">
                    <span className="truncate text-sm font-semibold">{heroName}</span>
                    <span className={cn("truncate text-xs", match.won ? "text-good" : "text-bad")}>
                      {result} · <time dateTime={match.started_at}>{day}</time>
                    </span>
                  </span>
                  <MatchScoreBadge score={score} compact />
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
      <FreshMatchesStatus />
      <Link
        to="/"
        className="inline-flex min-h-9 items-center gap-1.5 self-start px-2.5 text-[13px] font-semibold text-accent no-underline group-data-[state=collapsed]/sidebar:hidden hover:text-accent-strong"
      >
        <Trans>All matches</Trans>
        <ArrowRightIcon aria-hidden="true" className="size-4" />
      </Link>
    </SidebarGroup>
  );
};
